import { Activity, CalendarClock, CloudUpload, Inbox, Mail, Target, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { EnumeratorProgress, SurveyDetail, SurveyProgress } from '../api/types';
import { FieldNav, SurveyHeader } from '../components/SurveyHeader';
import { Empty, PageLoader } from '../components/ui';
import { formatDate, formatDay, formatNumber, percent, relativeTime } from '../format';
import { useI18n } from '../i18n';

const DAYS = 14;
const MATRIX_DAYS = 7;
const REFRESH_MS = 30_000;
// An enumerator who hasn't uploaded for this long is flagged.
const STALE_MS = 24 * 60 * 60 * 1000;

/** Local calendar day as YYYY-MM-DD (the API groups by the browser's time zone). */
const dayKey = (d: Date) => d.toLocaleDateString('en-CA');

function lastDayKeys(n: number): string[] {
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(dayKey(new Date(Date.now() - i * 86_400_000)));
  return out;
}

/** Field-work progress: who collected what and when, quota fill and invitation take-up. */
export function ProgressPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [progress, setProgress] = useState<SurveyProgress | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getSurvey(id).then(setSurvey, (e) => setError(e instanceof ApiError ? e.message : t('common.load_failed')));
  }, [id, t]);

  const canView = survey?.permissions.includes('responses.view');
  const load = useCallback(
    (silent = false) =>
      api.progress(id, DAYS).then(setProgress, (e) => {
        if (!silent) setError(e instanceof ApiError ? e.message : t('common.load_failed'));
      }),
    [id, t],
  );

  useEffect(() => {
    if (!canView) return;
    void load();
    const refresh = () => document.visibilityState === 'visible' && void load(true);
    const timer = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [canView, load]);

  if (error) return <div className="banner error">{error}</div>;
  if (!survey) return <PageLoader />;
  if (!canView) return <Navigate to={`/surveys/${id}/${survey.permissions.includes('survey.enumerators.assign') ? 'assignments' : 'share'}`} replace />;

  return (
    <>
      <SurveyHeader
        id={survey.id}
        number={survey.number}
        code={survey.code}
        status={progress?.status ?? survey.status}
        permissions={survey.permissions}
        active="field"
        title={<h1 className="ellipsis" style={{ fontSize: 26, fontWeight: 500 }}>{survey.title || t('common.untitled_questionnaire')}</h1>}
      />
      <FieldNav id={survey.id} permissions={survey.permissions} active="progress" />
      {!progress ? <PageLoader /> : <ProgressBody progress={progress} />}
    </>
  );
}

function ProgressBody({ progress: p }: { progress: SurveyProgress }) {
  const { t } = useI18n();
  const days = useMemo(() => lastDayKeys(DAYS), []);
  const perDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of p.daily) m.set(d.day.slice(0, 10), (m.get(d.day.slice(0, 10)) ?? 0) + d.count);
    return days.map((day) => ({ day, count: m.get(day) ?? 0 }));
  }, [p.daily, days]);
  const maxDay = Math.max(1, ...perDay.map((d) => d.count));
  const c = p.collection;
  const activeToday = p.enumerators.filter((e) => e.today > 0).length;

  return (
    <div className="stack gap-16">
      <ScheduleBanner progress={p} />

      <div className="card kpis">
        <div className="kpi">
          <div className="tile"><Inbox /></div>
          <div className="body">
            <div className="label">{t('progress.kpi_responses')}</div>
            <div className="value">
              {formatNumber(p.totals.responses)}
              {c.maxResponses != null && <small> / {formatNumber(c.maxResponses)}</small>}
            </div>
          </div>
        </div>
        <div className="kpi">
          <div className="tile"><Activity /></div>
          <div className="body">
            <div className="label">{t('progress.kpi_today')}</div>
            <div className="value">{formatNumber(p.totals.today)}</div>
          </div>
        </div>
        <div className="kpi">
          <div className="tile"><Users /></div>
          <div className="body">
            <div className="label">{t('progress.kpi_active')}</div>
            <div className="value">
              {formatNumber(activeToday)}
              <small> / {formatNumber(p.enumerators.length)}</small>
            </div>
          </div>
        </div>
        <div className="kpi">
          <div className="tile">{p.invites ? <Mail /> : <CloudUpload />}</div>
          <div className="body">
            <div className="label">{p.invites ? t('progress.kpi_invites') : t('progress.kpi_channels')}</div>
            <div className="value">
              {p.invites ? (
                <>
                  {percent(p.invites.completed, p.invites.total)}
                  <small> · {formatNumber(p.invites.completed)}/{formatNumber(p.invites.total)}</small>
                </>
              ) : (
                <>
                  {formatNumber(p.totals.web)}
                  <small> / {formatNumber(p.totals.mobile)}</small>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="grow">
            <h2>{t('progress.per_day')}</h2>
            <div className="sub">{t('progress.per_day_sub', { days: DAYS })}</div>
          </div>
        </div>
        <div className="card-pad" style={{ paddingTop: 0 }}>
          <div className="columns-chart" role="img" aria-label={t('progress.per_day')}>
            {perDay.map((d) => (
              <div key={d.day} className="col" tabIndex={0} data-tip={t('resp.day_tip', { date: formatDay(d.day), count: d.count })}>
                <div className={`bar ${d.count ? '' : 'zero'}`} style={{ height: `${(d.count / maxDay) * 100}%` }} />
              </div>
            ))}
          </div>
          <div className="x-axis" aria-hidden>
            {perDay.map((d, i) => (
              <span key={d.day}>{i % 2 === 1 ? formatDay(d.day) : ''}</span>
            ))}
          </div>
        </div>
      </div>

      <EnumeratorTable progress={p} />

      <div className="share-grid-sm">
        {p.quotas.length > 0 && (
          <div className="card">
            <div className="card-head">
              <div className="tile"><Target /></div>
              <div className="grow">
                <h2>{t('progress.quotas')}</h2>
                {c.closeOnQuotasFull && <div className="sub">{t('progress.quotas_close')}</div>}
              </div>
            </div>
            <div className="card-pad bar-list" style={{ paddingTop: 0 }}>
              {p.quotas.map((q) => (
                <div key={q.id} className="bar-row">
                  <span className="ellipsis">{q.name || q.id}</span>
                  <span className="n">
                    {formatNumber(q.count)} / {formatNumber(q.limit)} · {percent(Math.min(q.count, q.limit), q.limit)}
                  </span>
                  <div className="track" data-tip={`${q.name}: ${q.count}/${q.limit}`} tabIndex={0}>
                    <div className="fill" style={{ width: `${Math.min(100, (q.count / Math.max(1, q.limit)) * 100)}%`, display: q.count ? 'block' : 'none', background: q.count >= q.limit ? 'var(--data-2)' : undefined }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {p.invites && (
          <div className="card">
            <div className="card-head">
              <div className="tile"><Mail /></div>
              <h2 className="grow">{t('progress.invites')}</h2>
            </div>
            <div className="card-pad bar-list" style={{ paddingTop: 0 }}>
              {(['sent', 'opened', 'completed'] as const).map((k) => {
                // Funnel: anyone further along also counts as sent / opened.
                const n = k === 'sent' ? p.invites!.sent + p.invites!.opened + p.invites!.completed : k === 'opened' ? p.invites!.opened + p.invites!.completed : p.invites!.completed;
                return (
                  <div key={k} className="bar-row">
                    <span>{t(`progress.invites_${k}`)}</span>
                    <span className="n">
                      {formatNumber(n)} · {percent(n, p.invites!.total)}
                    </span>
                    <div className="track">
                      <div className="fill" style={{ width: `${(n / Math.max(1, p.invites!.total)) * 100}%`, display: n ? 'block' : 'none' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleBanner({ progress: p }: { progress: SurveyProgress }) {
  const { t } = useI18n();
  const c = p.collection;
  const now = Date.now();
  if (p.status === 'closed' && c.autoCloseReason) {
    return (
      <div className="banner warn">
        <CalendarClock /> {t(`progress.auto_closed_${c.autoCloseReason}`, { date: formatDate(c.autoClosedAt, true) })}
      </div>
    );
  }
  const parts: string[] = [];
  if (c.opensAt && new Date(c.opensAt).getTime() > now) parts.push(t('progress.opens_on', { date: formatDate(c.opensAt, true) }));
  if (c.closesAt) parts.push(t('progress.closes_on', { date: formatDate(c.closesAt, true) }));
  if (!parts.length) return null;
  return (
    <div className="banner">
      <CalendarClock /> {parts.join(' · ')}
    </div>
  );
}

function EnumeratorTable({ progress: p }: { progress: SurveyProgress }) {
  const { t } = useI18n();
  const days = useMemo(() => lastDayKeys(MATRIX_DAYS), []);
  const byUserDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of p.daily) if (d.userId) m.set(`${d.userId}|${d.day.slice(0, 10)}`, (m.get(`${d.userId}|${d.day.slice(0, 10)}`) ?? 0) + d.count);
    return m;
  }, [p.daily]);
  const maxCell = Math.max(1, ...byUserDay.values());

  if (!p.enumerators.length) {
    return (
      <div className="card">
        <Empty icon={<Users />} title={t('progress.no_enumerators')}>
          <p className="small">{t('progress.no_enumerators_body')}</p>
        </Empty>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="card-head">
        <div className="tile"><Users /></div>
        <div className="grow">
          <h2>{t('progress.enumerators')}</h2>
          <div className="sub">{t('progress.enumerators_sub', { days: MATRIX_DAYS })}</div>
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t('progress.col_enumerator')}</th>
              {days.map((d) => (
                <th key={d} className="num" style={{ fontSize: 11 }}>{formatDay(d)}</th>
              ))}
              <th className="num">{t('progress.col_total')}</th>
              <th>{t('progress.col_respondents')}</th>
              <th className="num">{t('progress.col_review')}</th>
              <th>{t('progress.col_last_upload')}</th>
            </tr>
          </thead>
          <tbody>
            {p.enumerators.map((e) => (
              <tr key={e.userId}>
                <td>
                  <div className="ellipsis">{e.name || e.email}</div>
                  <div className="small muted ellipsis">{e.email}</div>
                </td>
                {days.map((d) => {
                  const n = byUserDay.get(`${e.userId}|${d}`) ?? 0;
                  return (
                    <td key={d} className="num" style={{ background: n ? `color-mix(in srgb, var(--data-1) ${Math.round(15 + (n / maxCell) * 55)}%, transparent)` : undefined }}>
                      {n || <span className="muted">·</span>}
                    </td>
                  );
                })}
                <td className="num"><strong>{formatNumber(e.responses)}</strong></td>
                <td style={{ minWidth: 120 }}>
                  {e.assignedRespondents ? (
                    <div className="bar-row">
                      <span className="n">{e.completedRespondents} / {e.assignedRespondents}</span>
                      <span />
                      <div className="track">
                        <div className="fill" style={{ width: `${(e.completedRespondents / e.assignedRespondents) * 100}%`, display: e.completedRespondents ? 'block' : 'none' }} />
                      </div>
                    </div>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="num small">
                  <span title={t('progress.approved')}>✓ {e.approved}</span> · <span title={t('progress.rejected')}>✕ {e.rejected}</span>
                </td>
                <td><LastUpload e={e} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LastUpload({ e }: { e: EnumeratorProgress }) {
  const { t } = useI18n();
  const last = e.lastSyncAt ?? e.lastResponseAt;
  if (!last) return <span className="pill">{t('progress.never')}</span>;
  const stale = Date.now() - new Date(last).getTime() > STALE_MS;
  return (
    <span className={`pill ${stale ? 'orange' : 'green'}`} title={formatDate(last, true)}>
      {relativeTime(last)}
    </span>
  );
}
